/**
 * AttentionBanner - the nudge that stays on screen while work is waiting.
 *
 * It does not auto-dismiss and it has no timer. It leaves when the count
 * reaches zero, or when the operator puts it away with the Hide button, and
 * putting it away only lasts until the next arrival.
 *
 * ACCESSIBILITY, and it is load-bearing:
 *   - role="status" with aria-live="polite". Polite, never assertive: this
 *     count is re-read on a poll, and assertive would cut a screen reader off
 *     mid-sentence every single time.
 *   - Both controls are real buttons, so the banner is reachable, operable and
 *     dismissible from the keyboard with no shortcuts to learn.
 *   - The only animated thing is the small dot, at 0.6Hz, and it stops dead
 *     under prefers-reduced-motion. See the note at the top of
 *     assets/css/lusuku-overrides.css before changing anything visual here.
 */

import React, { useRef } from "react";
import usePublishedHeight from "../hooks/usePublishedHeight";

export const ATTENTION_BAR_VAR = "--lusuku-attention-h";

export default function AttentionBanner({
  count = 0,
  message = "",
  onOpen,
  onHide,
}) {
  const barRef = useRef(null);
  usePublishedHeight(barRef, count ? ATTENTION_BAR_VAR : null);
  if (!count) return null;

  return (
    <div ref={barRef} className="lsk-attention" role="status" aria-live="polite">
      <span className="lsk-attention__pulse" aria-hidden="true" />
      <div className="lsk-attention__body">
        <strong className="lsk-attention__title">
          {count === 1 ? "1 new order needs you" : `${count} new orders need you`}
        </strong>
        {message ? (
          <span className="lsk-attention__detail">{message}</span>
        ) : null}
      </div>
      <button type="button" className="lsk-attention__open" onClick={onOpen}>
        Open
      </button>
      <button
        type="button"
        className="lsk-attention__hide"
        onClick={onHide}
        title="Hide until the next new order arrives"
      >
        Hide
      </button>
    </div>
  );
}
